import { useEffect, useState } from "react";
import api, { apiErr, API } from "@/lib/api";
import { date } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Paperclip, Loader2, Plus } from "lucide-react";
import { SuperDelete } from "@/components/SuperDelete";
import { SuperEdit } from "@/components/SuperEdit";

export const fileHref = (f) => (f ? `${API.replace(/\/api$/, "")}${f.url}` : "#");

const STATUS_CLS = {
  pending: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  approved: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  rejected: "bg-red-500/20 text-red-300 border-red-500/40",
  cancelled: "bg-slate-700 text-slate-300 border-slate-600",
  valid: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  expiring: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  expired: "bg-red-500/20 text-red-300 border-red-500/40",
  not_started: "bg-slate-700 text-slate-300 border-slate-600",
  in_progress: "bg-sky-500/20 text-sky-300 border-sky-500/40",
  complete: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
};

export function Badge({ status, testid }) {
  return (
    <span data-testid={testid} className={`text-[10px] px-2 py-0.5 rounded border font-mono uppercase tracking-wider whitespace-nowrap ${STATUS_CLS[status] || STATUS_CLS.cancelled}`}>
      {String(status).replace("_", " ")}
    </span>
  );
}

export function FileLink({ file }) {
  if (!file) return null;
  return (
    <a href={fileHref(file)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid="hr-file-link">
      <Paperclip className="w-3 h-3" />{file.name}
    </a>
  );
}

export function UploadButton({ value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/hr/upload", fd);
      onChange(data); toast.success("File attached");
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="flex items-center gap-2">
      <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs px-3 py-2 rounded-md border border-border hover:bg-accent">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
        {value ? "Replace file" : "Attach photo / PDF"}
        <input type="file" accept="image/*,.pdf" className="hidden" onChange={pick} data-testid={testid} />
      </label>
      <FileLink file={value} />
    </div>
  );
}

export const useLeaveTypes = () => {
  const [types, setTypes] = useState([]);
  useEffect(() => { api.get("/hr/leave-types").then((r) => setTypes(r.data.filter((t) => t.active !== false))); }, []);
  return types;
};

export function useLeavePreview(s, e, sh, eh) {
  const [p, setP] = useState({ days: 0, holidays: [] });
  useEffect(() => {
    if (!s) { setP({ days: 0, holidays: [] }); return; }
    api.get("/hr/leave-preview", { params: { start: s, end: e || s, start_half: sh, end_half: eh } }).then((r) => setP(r.data)).catch(() => {});
  }, [s, e, sh, eh]);
  return p;
}

export function BalanceCard({ b }) {
  if (!b) return null;
  const cell = (l, v, cls = "", t) => (
    <div className="text-center"><div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{l}</div>
      <div className={`font-heading text-2xl font-extrabold font-mono-num ${cls}`} data-testid={t}>{v}</div></div>
  );
  return (
    <div className="card-industrial p-5" data-testid="balance-card">
      <div className="text-xs font-bold uppercase mb-3">Annual leave {b.year}</div>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
        {cell("Entitled", b.entitlement + b.carry_over + b.adjustments, "", "bal-entitled")}
        {cell("Taken", b.taken, "text-sky-300", "bal-taken")}
        {cell("Pending", b.pending, "text-amber-400", "bal-pending")}
        {cell("Balance", b.balance, b.balance < 0 ? "text-red-400" : "text-emerald-400", "bal-balance")}
        <div className="col-span-3 sm:col-span-1 text-[11px] text-muted-foreground self-center">
          {b.entitlement} base{b.carry_over ? ` + ${b.carry_over} carry` : ""}{b.adjustments ? ` ${b.adjustments > 0 ? "+" : ""}${b.adjustments} adj` : ""}
        </div>
      </div>
      {Object.keys(b.by_type).length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">{Object.entries(b.by_type).map(([k, v]) => <span key={k} className="text-[11px] px-2 py-1 rounded bg-accent">{k}: {v}d</span>)}</div>
      )}
    </div>
  );
}

export function LeaveForm({ userId, balance, onDone, trigger }) {
  const types = useLeaveTypes();
  const [open, setOpen] = useState(false);
  const blank = { type: "Annual Leave", start_date: "", end_date: "", start_half: false, end_half: false, note: "", attachment: null };
  const [f, setF] = useState(blank);
  const preview = useLeavePreview(f.start_date, f.end_date, f.start_half, f.end_half);
  const days = preview.days;
  const single = !f.end_date || f.end_date === f.start_date;
  const submit = async () => {
    try {
      await api.post("/hr/leave", { ...f, end_date: f.end_date || f.start_date, ...(userId ? { user_id: userId } : {}) });
      toast.success(userId ? "Leave added" : "Request submitted"); setOpen(false); setF(blank); onDone?.();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const chk = (k, label) => (
    <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.checked })} data-testid={`leave-${k}`} className="w-4 h-4 accent-orange-500" />{label}</label>
  );
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading uppercase">{userId ? "Add leave" : "Request leave"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Type</Label>
            <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}><SelectTrigger data-testid="leave-type"><SelectValue /></SelectTrigger>
              <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}</SelectContent></Select></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">First day</Label><Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="leave-start" className="h-12" /></div>
            <div><Label className="text-xs">Last day</Label><Input type="date" value={f.end_date} min={f.start_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} data-testid="leave-end" className="h-12" /></div>
          </div>
          <div className="flex flex-wrap gap-4">{chk("start_half", single ? "Half day" : "First day half")}{!single && chk("end_half", "Last day half")}</div>
          <div><Label className="text-xs">Note (optional)</Label><Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="leave-note" /></div>
          <UploadButton value={f.attachment} onChange={(a) => setF({ ...f, attachment: a })} testid="leave-attach" />
          <div className="rounded-lg bg-accent/50 p-3 text-sm flex justify-between" data-testid="leave-days-preview">
            <span>Working days</span><span className="font-mono-num font-bold">{days}</span>
          </div>
          {preview.holidays.length > 0 && (
            <p className="text-[11px] text-emerald-400" data-testid="leave-holidays-note">Not counted: {preview.holidays.map((h) => `${h.name} (${date(h.date)})`).join(", ")}</p>
          )}
          {balance && f.type === "Annual Leave" && (
            <p className="text-[11px] text-muted-foreground" data-testid="leave-balance-after">Balance after approval: <b className="text-foreground">{balance.balance - balance.pending - days}</b> days</p>
          )}
        </div>
        <DialogFooter><Button onClick={submit} disabled={!f.start_date || days <= 0} data-testid="leave-submit" className="w-full h-12 font-bold uppercase">{userId ? "Add (approved)" : "Submit request"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function LeaveList({ rows, onCancel, showName, actions, onDeleted }) {
  if (!rows.length) return <div className="text-center py-8 text-sm text-muted-foreground">No leave records.</div>;
  return (
    <div className="card-industrial divide-y divide-border" data-testid="leave-list">
      {rows.map((r) => (
        <div key={r.id} className="p-4 flex flex-wrap items-center gap-3 justify-between" data-testid={`leave-row-${r.id}`}>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {showName && <span className="font-bold">{r.user_name}</span>}
              <span className="text-sm font-semibold">{r.type}</span><Badge status={r.status} testid={`leave-status-${r.id}`} />
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {date(r.start_date)}{r.end_date !== r.start_date ? ` – ${date(r.end_date)}` : ""} · <b className="font-mono-num">{r.days}</b> day{r.days === 1 ? "" : "s"}
              {r.start_half ? " · half first" : ""}{r.end_half ? " · half last" : ""}
            </div>
            {r.note && <div className="text-xs mt-1">{r.note}</div>}
            {r.reason && r.status !== "pending" && <div className="text-xs mt-1 text-muted-foreground">{r.decided_by}: {r.reason}</div>}
            <FileLink file={r.attachment} />
            {(r.email_log || []).filter((e) => e.status === "failed").map((e, i) => (
              <div key={i} className="text-[11px] text-red-400 mt-1" data-testid={`leave-email-failed-${r.id}`}>Email ({e.kind}) to {e.to} failed to send</div>
            ))}
          </div>
          <div className="flex gap-2">
            {actions?.(r)}
            {onCancel && r.status === "pending" && <Button size="sm" variant="outline" onClick={() => onCancel(r)} data-testid={`leave-cancel-${r.id}`}>Cancel</Button>}
            <SuperEdit entity="leave" record={r} label="leave" onDone={onDeleted || (() => window.location.reload())} />
            <SuperDelete entity="leave" id={r.id} label={`${r.type} for ${r.user_name}`} onDone={onDeleted || (() => window.location.reload())} />
          </div>
        </div>
      ))}
    </div>
  );
}

export const AddButton = ({ label, testid, ...p }) => (
  <Button data-testid={testid} className="font-bold uppercase" {...p}><Plus className="w-4 h-4 mr-1" />{label}</Button>
);
